import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { SchemaSubDelay } from '@/__generated/es-api'
import { ERROR_SUBSCRIBE_TO_CAMPAIGN_SERVER } from '@/app/module/campaigns/definitions'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

export type SubscribeContactPayload = {
  contactId: number
  delay?: SchemaSubDelay
  startAt?: string
  t0?: string
  t0timezone?: string
}

/**
 * Subscribe a single contact to a campaign
 */
export const useSubscribeContact = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [subscribeContacts, res] = esApi.endpoints.subscribeContacts.useMutation()

  const subscribeContactsToCampaign = useCallback(
    async (campaignId: number, payload: SubscribeContactPayload) => {
      try {
        const result = await subscribeContacts({
          orgId: orgId.toString(),
          campaignId: campaignId.toString(),
          body: {
            allowResubscribe: true,
            args: {
              startAt: payload.startAt,
              t0: payload.t0,
              t0timezone: payload.t0timezone,
            },
            contacts: {
              includedContactIds: [payload.contactId],
            },
            delay: payload.delay,
          },
        }).unwrap()

        dispatch(
          createNotification({
            'campaign-subscribe-contact': {
              type: 'success',
              message: 'Contact successfully subscribed to campaign.',
            },
          }),
        )
        return result
      } catch (error) {
        dispatch(
          createNotification({
            'campaign-subscribe-contact-failure': {
              type: 'error',
              message: getErrorMessage(error, ERROR_SUBSCRIBE_TO_CAMPAIGN_SERVER),
            },
          }),
        )
        return undefined
      }
    },
    [orgId, dispatch, subscribeContacts],
  )

  return [subscribeContactsToCampaign, res] as const
}
